import { useMutation } from '@apollo/client'
import { CheckCircle2, KeyRound, Loader, Sparkles } from 'lucide-react'
import { Suspense, useTransition } from 'react'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { CopyField } from '@/components/sv/copy-field'
import { ToasterContent } from '@/components/sv/toaster-content'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { gql } from '@/lib/graphql/gql'
import { useCompanyPath, useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import type {
  GetOnboardingApiKeyRolesQuery,
  GetOnboardingApiKeyRolesQueryVariables,
  CreateOnboardingApiKeyMutation,
  CreateOnboardingApiKeyMutationVariables,
} from '@/components/onboarding-sheet/inline-api-key.types.gen'

const GET_ONBOARDING_API_KEY_ROLES = gql`
  query GetOnboardingApiKeyRoles($idCompany: String!) {
    company(id: $idCompany) {
      id
      apiKeyRoles {
        id
        name
        permissions
      }
    }
  }
`

const CREATE_ONBOARDING_API_KEY = gql`
  mutation CreateOnboardingApiKey(
    $idCompany: String!
    $name: String!
    $idRoles: [String!]
  ) {
    createApiKey(idCompany: $idCompany, name: $name, idRoles: $idRoles) {
      token
      name
    }
  }
`

type Props = {
  onCreated: (token: string) => void
  createdToken: string | null
}

const InlineApiKeyContent = ({ onCreated, createdToken }: Props) => {
  const { idCompany } = useIdCompany()
  const { getCompanyPath } = useCompanyPath()
  const [isPending, startTransition] = useTransition()

  const { data } = useSuspenseQuery<
    GetOnboardingApiKeyRolesQuery,
    GetOnboardingApiKeyRolesQueryVariables
  >(GET_ONBOARDING_API_KEY_ROLES, { variables: { idCompany } })

  const [createApiKey] = useMutation<
    CreateOnboardingApiKeyMutation,
    CreateOnboardingApiKeyMutationVariables
  >(CREATE_ONBOARDING_API_KEY, {
    refetchQueries: ['GetCompanyApiKeys', 'GetOwnerPublicApiKeys'],
  })

  const roles = data?.company?.apiKeyRoles ?? []
  const firstRole = roles[0]

  const handleCreate = () => {
    startTransition(async () => {
      try {
        const { data } = await createApiKey({
          variables: {
            idCompany,
            name: 'Onboarding API key',
            idRoles: firstRole ? [firstRole.id] : undefined,
          },
        })
        const token = data?.createApiKey?.token ?? ''
        if (token) {
          onCreated(token)
          toast(
            <ToasterContent variant="success">API key created</ToasterContent>,
            { position: 'top-right' }
          )
        }
      } catch {
        toast(
          <ToasterContent variant="error">
            Failed to create API key
          </ToasterContent>,
          { position: 'top-right' }
        )
      }
    })
  }

  if (createdToken) {
    return (
      <Alert>
        <CheckCircle2 />
        <AlertTitle>API key created — copy it now</AlertTitle>
        <AlertDescription className="justify-items-stretch gap-3">
          <CopyField value={createdToken} isSensitive className="w-full" />
          <p className="text-xs">
            This key uses your full account permissions. To customize access,{' '}
            <Link
              to={getCompanyPath({ pathname: '/api-keys' })}
              className="text-primary underline underline-offset-4 hover:text-primary/80"
            >
              manage API keys
            </Link>
            .
          </p>
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <Alert>
      <KeyRound />
      <AlertTitle>Create an API key</AlertTitle>
      <AlertDescription className="justify-items-stretch gap-3">
        <p>
          We'll create an API key with{' '}
          <strong className="text-foreground">
            {firstRole?.name ?? 'full'}
          </strong>{' '}
          access on your behalf. You can replace it with a different key any
          time from your{' '}
          <Link
            to={getCompanyPath({ pathname: '/api-keys' })}
            className="text-primary underline underline-offset-4 hover:text-primary/80"
          >
            API keys settings
          </Link>
          .
        </p>
        <Button size="sm" onClick={handleCreate} disabled={isPending}>
          {isPending ? (
            <Loader className="size-3.5 animate-spin" />
          ) : (
            <Sparkles className="size-3.5" />
          )}
          Create API key
        </Button>
      </AlertDescription>
    </Alert>
  )
}

export const InlineApiKey = ({ onCreated, createdToken }: Props) => {
  return (
    <Suspense
      fallback={
        <div className="rounded-lg border bg-card px-4 py-3 space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-9 w-full" />
        </div>
      }
    >
      <InlineApiKeyContent onCreated={onCreated} createdToken={createdToken} />
    </Suspense>
  )
}
